import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, RotateCcw, Play, Pause } from 'lucide-react';

const MovementPlayer = ({ config, taskTitle, onComplete, onClose }) => {
  const { steps, color } = config;
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [holdTimer, setHoldTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [currentRep, setCurrentRep] = useState(1);
  const intervalRef = useRef(null);
  const totalElapsedRef = useRef(0);

  const step = steps[currentIdx];
  const holdProgress = step ? Math.min(holdTimer / step.hold_seconds, 1) : 0;

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      totalElapsedRef.current += 1;
      setHoldTimer(prev => prev + 1);
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const nextAction = () => {
    if (currentRep < step.reps) { setCurrentRep(p => p + 1); setHoldTimer(0); }
    else if (currentIdx < steps.length - 1) { setCurrentIdx(p => p + 1); setCurrentRep(1); setHoldTimer(0); }
    else { setRunning(false); setFinished(true); }
  };

  const handleStart = () => { setStarted(true); setRunning(true); };
  const handleReset = () => {
    clearInterval(intervalRef.current);
    setCurrentIdx(0); setCurrentRep(1); setHoldTimer(0); setRunning(false);
    setStarted(false); setFinished(false); totalElapsedRef.current = 0;
  };

  const handleFinish = useCallback(() => {
    onComplete({
      started: true, duration_played: totalElapsedRef.current,
      steps_completed: currentIdx + 1, total_steps: steps.length, task_type: 'guided_movement',
    });
  }, [currentIdx, steps.length, onComplete]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="movement-player">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-2xl flex items-center justify-center" style={{
            background: `${color}15`, border: `1px solid ${color}25`, boxShadow: `0 0 50px ${color}15`,
          }}>
            <svg className="w-14 h-14" style={{ color }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M12 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM9 10h6l2 12H7l2-12z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm">{steps.length} hareket — adım adım rehberlik</p>
          </div>
          <button onClick={handleStart}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="movement-start">
            Başla
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Hareket Tamamlandı!</h2>
            <p className="text-white/40 text-sm">{steps.length} hareketi başarıyla tamamladın</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="movement-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full max-w-md gap-5">
          <div className="text-white/20 text-xs tracking-widest uppercase">Hareket {currentIdx + 1} / {steps.length}</div>

          <div key={`${currentIdx}-${currentRep}`} className="w-full p-6 rounded-2xl animate-fadeIn" style={{
            background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}20`, boxShadow: `0 0 30px ${color}08`,
          }}>
            <h3 className="text-lg font-bold mb-2" style={{ color }}>{step.title}</h3>
            <p className="text-white/70 text-sm leading-relaxed mb-4">{step.text}</p>
            {step.reps > 1 && <div className="text-white/30 text-xs mb-3">Tekrar {currentRep} / {step.reps}</div>}

            <div className="flex items-center justify-center">
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                  <circle cx="48" cy="48" r="42" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
                    strokeDasharray={`${holdProgress * 264} 264`}
                    style={{ transition: 'stroke-dasharray 1s linear' }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white/70 text-xl font-light tabular-nums">{holdTimer}s</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={running ? () => setRunning(false) : () => setRunning(true)}
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
              style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
              {running ? <Pause className="w-5 h-5" style={{ color }} /> : <Play className="w-5 h-5 ml-0.5" style={{ color }} />}
            </button>
            <button onClick={nextAction}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
              style={{ background: `${color}20`, border: `1px solid ${color}30` }}
              data-testid="movement-next">
              {currentIdx < steps.length - 1 || currentRep < step.reps ? (<>Sonraki <ChevronRight className="w-4 h-4" /></>) : 'Tamamla'}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div key={i} className="w-2 h-2 rounded-full transition-all duration-300" style={{
                background: i <= currentIdx ? color : 'rgba(255,255,255,0.08)',
                boxShadow: i === currentIdx ? `0 0 6px ${color}80` : 'none',
              }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MovementPlayer;
